package com.example.mystybag.login.views

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import dev.icerock.moko.resources.compose.painterResource
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.keyboardPadding
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.core.resources.SRC
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.defines.TextFieldType
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.rememberUseNativeStyleWebLayout
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.login.views.components.MerchantRegistrationProgressBar
import com.example.mystybag.login.views.components.MerchantRegistrationProgressFractions
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.login.logic.MerchantRegistrationAddLoginDetailsViewModel
import com.example.mystybag.login.model.LoginDetailsRequiredField
import com.example.mystybag.login.views.views.MerchantRegistrationAddLoginDetailsWebView
import dev.icerock.moko.resources.compose.stringResource

@Composable
fun MerchantRegistrationAddLoginDetailsView(viewModel: MerchantRegistrationAddLoginDetailsViewModel) {
    if (PlatformHelper.isWeb) {
        MerchantRegistrationAddLoginDetailsWebView(viewModel)
        return
    }
    UIView(
        modifier = Modifier.fillMaxSize(),
        viewModel = viewModel,
        horizontalAlignment = Alignment.Start
    ) {
        MerchantRegistrationAddLoginDetailsForm(viewModel)
    }
}

@Composable
internal fun MerchantRegistrationAddLoginDetailsForm(
    viewModel: MerchantRegistrationAddLoginDetailsViewModel
) {
    val pinContinueToBottom = rememberUseNativeStyleWebLayout()
    val scrollState = rememberScrollState()
    if (pinContinueToBottom) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .fillMaxWidth()
                .keyboardPadding()
                .padding(bottom = p8)
        ) {
            Column(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .fillMaxHeight()
                    .verticalScroll(scrollState),
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.Top
            ) {
                MerchantRegistrationAddLoginDetailsScrollableBody(viewModel)
            }
            MerchantRegistrationAddLoginDetailsFooter(viewModel)
        }
    } else {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(scrollState)
                .keyboardPadding()
                .padding(bottom = p8),
            horizontalAlignment = Alignment.Start,
            verticalArrangement = Arrangement.Top
        ) {
            MerchantRegistrationAddLoginDetailsScrollableBody(viewModel)
            MerchantRegistrationAddLoginDetailsFooter(viewModel)
        }
    }
}

@Composable
internal fun MerchantRegistrationAddLoginDetailsScrollableBody(
    viewModel: MerchantRegistrationAddLoginDetailsViewModel
) {
    val state by viewModel.uiState.subscribeAsState()
    MerchantRegistrationProgressBar(MerchantRegistrationProgressFractions.LOGIN_DETAILS)

    Spacer(modifier = Modifier.height(p32))

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16),
        horizontalAlignment = Alignment.Start
    ) {
        Texts.DisplaySmall(
            text = stringResource(SR.strings.add_your_login_details),
            textAlign = TextAlign.Start,
            modifier = Modifier.fillMaxWidth()
        )
    }

    Spacer(modifier = Modifier.height(p16))

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16),
        verticalArrangement = Arrangement.spacedBy(p16)
    ) {
        DefaultCircleShapedTextField(
            value = state.email,
            onValueChange = viewModel::updateEmail,
            label = stringResource(SR.strings.email_address),
            modifier = Modifier.fillMaxWidth(),
            inputType = TextFieldType.EMAIL,
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Email,
                imeAction = ImeAction.Done
            ),
            isError = LoginDetailsRequiredField.EMAIL in state.requiredFieldErrors,
            errorText = ""
        )

        DefaultCircleShapedTextField(
            value = state.password,
            onValueChange = viewModel::updatePassword,
            label = stringResource(SR.strings.password),
            modifier = Modifier.fillMaxWidth(),
            isError = LoginDetailsRequiredField.PASSWORD in state.requiredFieldErrors,
            errorText = "",
            visualTransformation = if (state.passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
            trailingIcon = {
                Image(
                    painter = painterResource(
                        if (state.passwordVisible) SRC.images.password_eye_open else SRC.images.password_eye_closed
                    ),
                    modifier = Modifier
                        .size(p24)
                        .noRippleClickable { viewModel.togglePasswordVisibility() }
                )
            },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password)
        )

        Column(verticalArrangement = Arrangement.spacedBy(p8)) {
            PasswordRequirementItem(
                text = stringResource(SR.strings.at_least_8_characters),
                isValid = state.passwordRequirements.hasMinLength
            )
            PasswordRequirementItem(
                text = stringResource(SR.strings.at_least_1_number),
                isValid = state.passwordRequirements.hasNumber
            )
            PasswordRequirementItem(
                text = stringResource(SR.strings.at_least_1_letter),
                isValid = state.passwordRequirements.hasLetter
            )
        }

        DefaultCircleShapedTextField(
            value = state.confirmPassword,
            onValueChange = viewModel::updateConfirmPassword,
            label = stringResource(SR.strings.confirm_password),
            modifier = Modifier.fillMaxWidth(),
            isError = LoginDetailsRequiredField.CONFIRM_PASSWORD in state.requiredFieldErrors,
            errorText = "",
            visualTransformation = if (state.confirmPasswordVisible) VisualTransformation.None else PasswordVisualTransformation(),
            trailingIcon = {
                Image(
                    painter = painterResource(
                        if (state.confirmPasswordVisible) SRC.images.password_eye_open else SRC.images.password_eye_closed
                    ),
                    modifier = Modifier
                        .size(p24)
                        .noRippleClickable { viewModel.toggleConfirmPasswordVisibility() }
                )
            },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password)
        )
    }
}

@Composable
internal fun MerchantRegistrationAddLoginDetailsFooter(
    viewModel: MerchantRegistrationAddLoginDetailsViewModel
) {
    val state by viewModel.uiState.subscribeAsState()
    Column(modifier = Modifier.fillMaxWidth()) {
        Spacer(modifier = Modifier.height(p32))
        DefaultButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = p16, vertical = p16),
            type = DefaultButtonType.PRIMARY,
            caption = stringResource(SR.strings.next),
            callback = viewModel::onContinue,
            enabled = !state.isLoading
        )
    }
}

@Composable
private fun PasswordRequirementItem(text: String, isValid: Boolean) {
    val color = if (isValid) AppTheme.current.colors.primary else AppTheme.current.colors.onSurfaceVariant
    Row(verticalAlignment = Alignment.CenterVertically) {
        Texts.BodyMedium(
            text = if (isValid) "✓" else "✗",
            modifier = Modifier.padding(end = p8),
            color = color
        )
        Texts.BodyMedium(text = text, color = color)
    }
}
